import React from "react";
import EventImage from "../../../../components/EventImage.jsx";
import { FaTicketAlt, FaCalendarAlt, FaUsers, FaMapMarkerAlt } from "react-icons/fa";
import { fixS3ImageUrl } from "../../../../components/imageUtils";

const getBackgroundStyle = (rawBackground) => {
  if (!rawBackground) {
    return {};
  }

  let background = rawBackground;

  // Allow background to be either a real array, JSON-stringified array,
  // or a Postgres text[] string like {image,"https://..."}
  if (!Array.isArray(background) && typeof background === "string") {
    const trimmed = background.trim();

    // First, try JSON.parse (e.g. '["image","https://..."]')
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        background = parsed;
      }
    } catch {
      // If it's not JSON, handle Postgres array syntax
      if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
        const inner = trimmed.slice(1, -1);
        const parts = inner.split(",");
        if (parts.length > 0) {
          background = parts.map((p) => {
            const t = p.trim();
            if ((t.startsWith('"') && t.endsWith('"')) || (t.startsWith("'") && t.endsWith("'"))) {
              return t.slice(1, -1);
            }
            return t;
          });
        }
      }
    }
  }

  if (!Array.isArray(background) || background.length === 0) {
    return {};
  }

  const mode = background[0];
  const value1 = background[1];
  const value2 = background[2];

  if (mode === "solid" && value1) {
    return {
      backgroundColor: value1,
      backgroundImage: "none",
    };
  }

  if (mode === "gradient" && value1 && value2) {
    return {
      backgroundImage: `linear-gradient(90deg, ${value1}, ${value2})`,
      backgroundColor: "transparent",
    };
  }

  if (mode === "image" && value1) {
    const fixedBgUrl = fixS3ImageUrl(value1);
    return {
      backgroundImage: `url(${fixedBgUrl})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      backgroundColor: "transparent",
    };
  }

  return {};
};

const PurchaseCard = ({ eventGroup, formatDate, getFormattedLatestDate, formatPrice, onViewTickets }) => {
  const handleClick = () => {
    onViewTickets(eventGroup);
  };

  return (
    <div
      className="purchase-card"
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <div
        className="purchase-card__image-wrapper"
        style={getBackgroundStyle(eventGroup.background)}
      >
        <EventImage
          image={eventGroup.eventImage}
          coverimage={eventGroup.eventImage}
          alt={eventGroup.eventName}
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
          loading="eager"
        />
      </div>

      <div className="purchase-card__body">
        <h3 className="purchase-card__title">{eventGroup.eventName}</h3>

        <div className="purchase-card__meta">
          {eventGroup.eventDate && (
            <div className="purchase-card__meta-row">
              <FaCalendarAlt className="purchase-card__meta-icon" />
              <span>Event Date: {formatDate(eventGroup.eventDate)}</span>
            </div>
          )}

          {eventGroup.eventLocation && (
            <div className="purchase-card__meta-row">
              <FaMapMarkerAlt className="purchase-card__meta-icon" />
              <span>Location: {eventGroup.eventLocation}</span>
            </div>
          )}

          <div className="purchase-card__meta-row">
            <FaCalendarAlt className="purchase-card__meta-icon" />
            <span>Latest Purchase: {getFormattedLatestDate(eventGroup.purchases)}</span>
          </div>

          <div className="purchase-card__meta-row">
            <FaUsers className="purchase-card__meta-icon" />
            <span>
              {eventGroup.purchases.length} purchase
              {eventGroup.purchases.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>

        <div className="purchase-card__bottom">
          <div className="purchase-card__total">Total: {formatPrice(eventGroup.totalAmount)}</div>
          <button
            type="button"
            className="purchase-card__button"
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
          >
            <FaTicketAlt /> View Tickets
          </button>
        </div>
      </div>
    </div>
  );
};

export default PurchaseCard;
